// --- UPDATED FILE: components/src/modals/VersionHistoryModal.tsx ---

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDistanceToNow } from 'date-fns';
import { GitCommit, User, Clock, Code, Undo } from 'lucide-react';

import { Modal, Button, Spinner } from '@dopomogai/ui';
import { useVersionHistory } from '@dopomogai/agent-kit';
import type { RevertPayload, UUID } from '@dopomogai/types';

// ========================================================================
// ==                      PROPS INTERFACE                               ==
// ========================================================================

interface VersionHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  entityType?: 'project' | 'sprint' | 'task';
  entityId?: UUID;
  onRevert: (
    entityType: 'project' | 'sprint' | 'task',
    entityId: UUID,
    payload: RevertPayload
  ) => Promise<void>;
}

// ========================================================================
// ==                      INTERNAL SUB-COMPONENTS                       ==
// ========================================================================

const SnapshotViewerModal: React.FC<{ snapshot: any; onClose: () => void }> = ({ snapshot, onClose }) => (
    <Modal isOpen={true} onClose={onClose} title="Version Snapshot Data" size="2xl">
      <div className="p-4 bg-slate-800 rounded-lg max-h-[60vh] overflow-auto">
        <pre className="text-sm text-slate-200">
          <code>{JSON.stringify(snapshot, null, 2)}</code>
        </pre>
      </div>
      <div className="mt-4 flex justify-end">
        <Button text="Close" onClick={onClose} variant="secondary" />
      </div>
    </Modal>
);

const VersionHistoryItem: React.FC<{
  version: any;
  onRevertClick: () => void;
  onViewSnapshotClick?: () => void; // This prop is optional
}> = ({ version, onRevertClick, onViewSnapshotClick }) => {
  return (
    <div className="flex items-start gap-4 py-4 border-b border-slate-200 last:border-b-0">
      <div className="flex-shrink-0 flex flex-col items-center">
        <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center font-bold text-slate-600 border-2 border-slate-200">
          v{version.version}
        </div>
        <div className="w-px h-4 bg-slate-200 mt-2" />
      </div>
      <div className="flex-grow">
        <p className="font-semibold text-slate-800">{version.commit_message || 'Initial Version'}</p>
        <div className="flex items-center gap-4 text-xs text-slate-500 mt-1">
          {/* --- FIX: Defensively check for `version.id` before calling slice --- */}
          {version.id && <span className="flex items-center gap-1.5"><GitCommit size={12} /> {version.id.slice(0, 8)}</span>}
          <span className="flex items-center gap-1.5"><User size={12} /> {version.editor_id ? `User ${version.editor_id.slice(0, 8)}...` : 'System'}</span>
          <span className="flex items-center gap-1.5"><Clock size={12} /> {formatDistanceToNow(new Date(version.created_at), { addSuffix: true })}</span>
        </div>
      </div>
      <div className="flex-shrink-0 flex items-center gap-2">
        {/* --- FIX: Conditionally render the "Snapshot" button based on the prop --- */}
        {onViewSnapshotClick && (
            <Button text="Snapshot" icon={<Code size={14}/>} variant="outline" size="sm" onClick={onViewSnapshotClick} />
        )}
        <Button text="Revert" icon={<Undo size={14}/>} variant="secondary" size="sm" onClick={onRevertClick} />
      </div>
    </div>
  );
};


// ========================================================================
// ==                       MAIN EXPORTED COMPONENT                      ==
// ========================================================================

export const VersionHistoryModal: React.FC<VersionHistoryModalProps> = ({
  isOpen,
  onClose,
  entityType,
  entityId,
  onRevert,
}) => {
  const { t } = useTranslation(['common', 'admin_shared']);
  const { data: versions, isLoading, error } = useVersionHistory(entityType, entityId);
  const [snapshotToShow, setSnapshotToShow] = useState<any | null>(null);

  const handleRevertClick = (version: any) => {
    const commitMessage = window.prompt(t('history.revertPrompt', { version: version.version }));
    if (commitMessage && entityType && entityId) {
        const payload: RevertPayload = {
            version: version.version,
            commit_message: commitMessage,
        };
        onRevert(entityType, entityId, payload);
    }
  };

  const renderContent = () => {
    if (isLoading) return <div className="h-48 flex items-center justify-center"><Spinner text={t('history.loading')} /></div>;
    if (error) return <div className="h-48 flex items-center justify-center text-red-600"><p>{t('history.error', { message: error.message })}</p></div>;
    if (!versions || versions.length === 0) return <div className="h-48 flex items-center justify-center text-slate-500"><p>{t('history.empty')}</p></div>;

    return (
      <div className="space-y-2">
        {versions.map((v: any) => (
          <VersionHistoryItem
            key={v.id || v.version} // Use version as a fallback key
            version={v}
            // --- FIX: Centralize the logic for checking the snapshot property here ---
            // This is the most important fix. We check if the `snapshot` property
            // exists on the version object before passing the click handler down.
            onViewSnapshotClick={
              'snapshot' in v && v.snapshot
                ? () => setSnapshotToShow(v.snapshot)
                : undefined // Pass undefined if no snapshot exists
            }
            onRevertClick={() => handleRevertClick(v)}
          />
        ))}
      </div>
    );
  };
  
  const entityName = entityType ? t(`entity_${entityType}`, { ns: 'common' }) : 'History';

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={`${entityName} History`}
        size="3xl"
      >
        <div className="max-h-[70vh] overflow-y-auto -mr-4 pr-4">
            {renderContent()}
        </div>
      </Modal>
      
      {snapshotToShow && (
        <SnapshotViewerModal 
            snapshot={snapshotToShow}
            onClose={() => setSnapshotToShow(null)}
        />
      )}
    </>
  );
};